import type { ReactNode } from "react";
import type * as THREE from "three";
import { useOutlineMaterial, OUTLINE_COLOR } from "./outlineMaterial";

interface OutlinedProps {
  /** Geometry shared between the visible mesh (in children) and the outline hull. */
  geometry: THREE.BufferGeometry;
  thickness?: number;
  color?: string;
  children: ReactNode;
}

/**
 * Wraps a toon-shaded mesh with a thick, crisp inverted-hull outline —
 * `04-art-direction.md`'s "thick, crisp line art". Pass the same geometry instance
 * used by the visible mesh in `children` so the hull matches exactly.
 *
 * Usage:
 *   <Outlined geometry={geo}>
 *     <mesh geometry={geo} material={toonMaterial} />
 *   </Outlined>
 */
export function Outlined({ geometry, thickness = 0.015, color = OUTLINE_COLOR, children }: OutlinedProps) {
  const outlineMaterial = useOutlineMaterial(color, thickness);
  return (
    <group>
      {children}
      <mesh geometry={geometry} material={outlineMaterial} renderOrder={-1} />
    </group>
  );
}
